import { c as _c } from "react/compiler-runtime";
import { Checkbox } from '@ark-ui/react/checkbox';
import { Field } from '@ark-ui/react/field';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { css, cva, cx } from '@repo/panda/css';
import { Icon } from '../ds/icon';
import { generateUniqueId } from '../utils/accessibility';
import { Type } from './type';
const checkboxContainerStyles = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '4',
    cursor: 'pointer',
    width: 'fit',
    position: 'relative'
  },
  variants: {
    fullWidth: {
      true: {
        width: 'full'
      },
      false: {
        width: 'fit'
      }
    },
    disabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5
      },
      false: {}
    }
  },
  defaultVariants: {
    fullWidth: false,
    disabled: false
  }
});
const checkboxControlStyles = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: '1px',
    borderStyle: 'solid',
    transition: 'all',
    borderRadius: '6',
    cursor: 'pointer',
    flexShrink: 0,
    _focusVisible: {
      ring: '[2px]',
      ringColor: 'blue.500',
      ringOffset: '[1px]'
    }
  },
  variants: {
    variant: {
      dark: {
        backgroundColor: 'whiteAlpha.5',
        borderColor: 'whiteAlpha.40',
        _hover: {
          borderColor: 'whiteAlpha.20'
        },
        '&[data-state=checked]': {
          backgroundColor: 'blue.500',
          borderColor: 'blue.500',
          _hover: {
            backgroundColor: 'blue.600',
            borderColor: 'blue.600'
          }
        },
        '&[data-state=indeterminate]': {
          backgroundColor: 'blue.500',
          borderColor: 'blue.500'
        }
      },
      light: {
        backgroundColor: 'blackAlpha.5',
        borderColor: 'blackAlpha.15',
        _hover: {
          borderColor: 'grey.400'
        },
        '&[data-state=checked]': {
          backgroundColor: 'blue.500',
          borderColor: 'blue.500',
          _hover: {
            backgroundColor: 'blue.600',
            borderColor: 'blue.600'
          }
        },
        '&[data-state=indeterminate]': {
          backgroundColor: 'blue.500',
          borderColor: 'blue.500'
        }
      },
      brand: {
        backgroundColor: 'whiteAlpha.5',
        borderColor: 'whiteAlpha.15',
        borderWidth: '[1.5px]',
        borderRadius: '4',
        _hover: {
          borderColor: 'whiteAlpha.20'
        },
        '&[data-state=checked]': {
          backgroundColor: 'brand.primary',
          borderColor: 'brand.primary',
          _hover: {
            backgroundColor: 'brand.primary',
            borderColor: 'brand.primary'
          }
        },
        '&[data-state=indeterminate]': {
          backgroundColor: 'brand.primary',
          borderColor: 'brand.primary'
        }
      }
    },
    size: {
      sm: {
        width: '[20px]',
        height: '[20px]'
      },
      md: {
        width: '[24px]',
        height: '[24px]'
      },
      lg: {
        width: '[26px]',
        height: '[26px]'
      }
    },
    disabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
        _hover: {
          borderColor: '[inherit]',
          backgroundColor: '[inherit]'
        }
      },
      false: {}
    }
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md',
    disabled: false
  }
});
const checkboxIndicatorStyles = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white'
  },
  variants: {
    size: {
      sm: {},
      md: {},
      lg: {}
    }
  },
  defaultVariants: {
    size: 'md'
  }
});
const checkboxLabelStyles = cva({
  base: {
    cursor: 'pointer',
    userSelect: 'none'
  },
  variants: {
    variant: {
      dark: {
        color: 'white'
      },
      light: {
        color: 'text.primary'
      },
      brand: {
        color: 'white'
      }
    },
    size: {
      sm: {},
      md: {},
      lg: {}
    },
    disabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5
      },
      false: {}
    },
    required: {
      true: {
        _after: {
          content: '" *"',
          color: 'error'
        }
      },
      false: {}
    }
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md',
    disabled: false,
    required: false
  }
});
const errorStyles = cva({
  base: {
    fontSize: 'sm',
    marginTop: '4'
  },
  variants: {
    variant: {
      dark: {
        color: 'error'
      },
      light: {
        color: 'error.600'
      },
      brand: {
        color: 'error'
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
const helperTextStyles = cva({
  base: {
    fontSize: 'sm',
    marginTop: '4'
  },
  variants: {
    variant: {
      dark: {
        color: 'whiteAlpha.70'
      },
      light: {
        color: 'grey.600'
      },
      brand: {
        color: 'whiteAlpha.70'
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
export type CheckboxVariants = NonNullable<Parameters<typeof checkboxControlStyles>[0]>;
export interface CheckboxProps extends Omit<Checkbox.RootProps, 'className'> {
  label?: string;
  variant?: CheckboxVariants['variant'];
  size?: CheckboxVariants['size'];
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  errorText?: string;
  helperText?: string;
  className?: string;
  checked?: boolean | 'indeterminate';
  onCheckedChange?: (details: Checkbox.CheckedChangeDetails) => void;
  id: string;
}
export const CheckboxComponent = t0 => {
  const $ = _c(89);
  let arkProps;
  let checked;
  let className;
  let errorText;
  let helperText;
  let id;
  let label;
  let onCheckedChange;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      label,
      variant: t1,
      size: t2,
      disabled: t3,
      required: t4,
      fullWidth: t5,
      errorText,
      helperText,
      className,
      checked,
      onCheckedChange,
      id,
      ...arkProps
    } = t0);
    $[0] = t0;
    $[1] = arkProps;
    $[2] = checked;
    $[3] = className;
    $[4] = errorText;
    $[5] = helperText;
    $[6] = id;
    $[7] = label;
    $[8] = onCheckedChange;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    arkProps = $[1];
    checked = $[2];
    className = $[3];
    errorText = $[4];
    helperText = $[5];
    id = $[6];
    label = $[7];
    onCheckedChange = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
  }
  const variant = t1 === undefined ? "dark" : t1;
  const size = t2 === undefined ? "md" : t2;
  const disabled = t3 === undefined ? false : t3;
  const required = t4 === undefined ? false : t4;
  const fullWidth = t5 === undefined ? false : t5;
  const hasError = Boolean(errorText);
  const hasHelperText = Boolean(helperText);
  let t6;
  if ($[14] !== hasError) {
    t6 = hasError ? generateUniqueId("checkbox-error") : undefined;
    $[14] = hasError;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const errorId = t6;
  let t7;
  if ($[16] !== hasHelperText) {
    t7 = hasHelperText ? generateUniqueId("checkbox-helper") : undefined;
    $[16] = hasHelperText;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const helperTextId = t7;
  const getIconSize = _temp;
  let t8;
  if ($[18] !== errorId || $[19] !== helperTextId) {
    t8 = [errorId, helperTextId].filter(Boolean).join(" ") || undefined;
    $[18] = errorId;
    $[19] = helperTextId;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const ariaDescribedBy = t8;
  let t9;
  if ($[21] !== variant) {
    t9 = variant === "brand" ? css({
      color: "black"
    }) : css({
      color: "white"
    });
    $[21] = variant;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const indicatorColorStyles = t9;
  let t10;
  if ($[23] !== disabled || $[24] !== fullWidth) {
    t10 = checkboxContainerStyles({
      fullWidth,
      disabled
    });
    $[23] = disabled;
    $[24] = fullWidth;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== className) {
    t11 = cx(className);
    $[26] = className;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== disabled || $[29] !== size || $[30] !== variant) {
    t12 = checkboxControlStyles({
      variant,
      size,
      disabled
    });
    $[28] = disabled;
    $[29] = size;
    $[30] = variant;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== size) {
    t13 = checkboxIndicatorStyles({
      size
    });
    $[32] = size;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const t14 = getIconSize(size);
  let t15;
  if ($[34] !== indicatorColorStyles || $[35] !== t14) {
    t15 = <Icon icon="check" size={t14} className={indicatorColorStyles} decorative={true} />;
    $[34] = indicatorColorStyles;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t13 || $[38] !== t15) {
    t16 = <Checkbox.Indicator className={t13}>{t15}</Checkbox.Indicator>;
    $[37] = t13;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== size) {
    t17 = checkboxIndicatorStyles({
      size
    });
    $[40] = size;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const t18 = getIconSize(size);
  let t19;
  if ($[42] !== indicatorColorStyles || $[43] !== t18) {
    t19 = <Icon icon="remove" size={t18} className={indicatorColorStyles} decorative={true} />;
    $[42] = indicatorColorStyles;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t17 || $[46] !== t19) {
    t20 = <Checkbox.Indicator indeterminate={true} className={t17}>{t19}</Checkbox.Indicator>;
    $[45] = t17;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t12 || $[49] !== t16 || $[50] !== t20) {
    t21 = <Checkbox.Control className={t12}>{t16}{t20}</Checkbox.Control>;
    $[48] = t12;
    $[49] = t16;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Checkbox.HiddenInput />;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== disabled || $[54] !== label || $[55] !== required || $[56] !== size || $[57] !== variant) {
    t23 = label && <Checkbox.Label className={checkboxLabelStyles({
      variant,
      size,
      disabled,
      required
    })}><Type variant="body-s">{label}</Type></Checkbox.Label>;
    $[53] = disabled;
    $[54] = label;
    $[55] = required;
    $[56] = size;
    $[57] = variant;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t21 || $[60] !== t23) {
    t24 = <FlexRow gap="8" align="center">{t21}{t22}{t23}</FlexRow>;
    $[59] = t21;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== ariaDescribedBy || $[63] !== arkProps || $[64] !== checked || $[65] !== disabled || $[66] !== hasError || $[67] !== id || $[68] !== onCheckedChange || $[69] !== required || $[70] !== t11 || $[71] !== t24) {
    t25 = <Field.Root><Checkbox.Root id={id} disabled={disabled} required={required} checked={checked} onCheckedChange={onCheckedChange} aria-describedby={ariaDescribedBy} aria-invalid={hasError} className={t11} {...arkProps}>{t24}</Checkbox.Root></Field.Root>;
    $[62] = ariaDescribedBy;
    $[63] = arkProps;
    $[64] = checked;
    $[65] = disabled;
    $[66] = hasError;
    $[67] = id;
    $[68] = onCheckedChange;
    $[69] = required;
    $[70] = t11;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== hasError || $[74] !== hasHelperText || $[75] !== helperText || $[76] !== helperTextId || $[77] !== variant) {
    t26 = hasHelperText && !hasError && <div id={helperTextId} className={helperTextStyles({
      variant
    })}>{helperText}</div>;
    $[73] = hasError;
    $[74] = hasHelperText;
    $[75] = helperText;
    $[76] = helperTextId;
    $[77] = variant;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== errorId || $[80] !== errorText || $[81] !== hasError || $[82] !== variant) {
    t27 = hasError && <div id={errorId} className={errorStyles({
      variant
    })} role="alert" aria-live="polite">{errorText}</div>;
    $[79] = errorId;
    $[80] = errorText;
    $[81] = hasError;
    $[82] = variant;
    $[83] = t27;
  } else {
    t27 = $[83];
  }
  let t28;
  if ($[84] !== t10 || $[85] !== t25 || $[86] !== t26 || $[87] !== t27) {
    t28 = <div className={t10}>{t25}{t26}{t27}</div>;
    $[84] = t10;
    $[85] = t25;
    $[86] = t26;
    $[87] = t27;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  return t28;
};

// Export with a more standard name
export { CheckboxComponent as Checkbox };
function _temp(checkboxSize) {
  switch (checkboxSize) {
    case "sm":
      {
        return "sm";
      }
    case "md":
      {
        return "sm";
      }
    case "lg":
      {
        return "md";
      }
    default:
      {
        return "sm";
      }
  }
}